<script lang="ts" setup>
import type {Area} from "#magnolia-layer/types/magnolia-types";
import {type Delivery, isLinkTypeExternal} from "#magnolia-layer/types/api";
import type {Page, Partner} from "~/types/mgnl";
import usePageQueryRef from "~/composables/usePageQueryRef";
import {useOffsetPagination} from "@vueuse/core";
import ErrorDisplayConcise from "~/components/ErrorDisplayConcise.vue";

const props = defineProps<{
  metadata: Area;
  rootLink: string;
}>();

const pageRef = usePageQueryRef();
const limit = ref(8);
const offset = computed(() => {
  return (pageRef.value - 1) * limit.value;
});

const runtimeConfig = useRuntimeConfig();
const context = useMagnoliaContext();
const {data: content, error} = await useFetch<Delivery<Partner>>(
  "/api/paginatedChildPages",
  {
    params: {
      deliveryApi: runtimeConfig.public.mgnl.apiPartners,
      offset: offset,
      limit: limit,
      path: props.rootLink,
      lang: context.searchParams?.lang,
    },
    watch: [offset],
  },
);

const offsetPagination = useOffsetPagination({
  page: pageRef,
  total: content.value?.total,
  pageSize: limit.value,
});
usePaginationHeadLinks(offsetPagination);
useErrorStatusForwarding(error);

const getLink = (partner: Partner) => {
  return resolveLink(partner);
};
</script>

<template>
  <section class="col-span-full content-grid py-22">
    <h1 class="sr-only">{{ $t("label.overview") }}</h1>
    <ErrorDisplayConcise v-if="error" :error></ErrorDisplayConcise>
    <div v-else class="articles grid grid-cols-12 gap-3 items-end">
      <template v-for="(page, index) in content?.results" :key="index">
        <TextMediaLayout
          :compact="true"
          :border="false"
          :linkText="
            isLinkTypeExternal(page.link)
              ? $t('label.visitWebsite')
              : $t('label.continueReading')
          "
          :link="getLink(page)"
          :category="undefined"
          :title="page.title"
          :role="page.type">
          <template #media>
            <MagnoliaPicture
              :asset="page.image"
              :class="{'grayscale brightness-[10]': page.logoIsDiaP != true}"
              :modifiers="{}"
              loading="lazy"
              sizes="125,250"
              v-if="page.image">
            </MagnoliaPicture>
          </template>
          <template v-if="page.description">{{ page.description }}</template>
        </TextMediaLayout>
      </template>
    </div>
    <Pagination
      v-if="offsetPagination.pageCount.value > 1"
      :enable-pages="false"
      :enable-links="true"
      :pagination="offsetPagination"
      :total="content?.total ?? 0"></Pagination>
  </section>
</template>

<style lang="scss" scoped></style>
